import type { CSSProperties } from 'react'
import { Plus } from 'lucide-react'
import { useNav } from '@/shared/hooks/use-nav'
import { TopBar } from '@/shared/components/top-bar'
import { SegmentedControl } from '@/shared/ui/segmented'
import { CourtBookingsSection } from './court-bookings-section'
import { MatchCard } from '@/shared/components/match-card'
import { MatchCardSkeleton } from '@/shared/components/match-card-skeleton'
import { EmptyState } from '@/shared/ui/empty-state'
import { ErrorState } from '@/shared/ui/error-state'
import { Button } from '@/shared/ui/button'
import { useMisPartidos, type MisTab } from './use-mis-partidos'

// My matches — upcoming vs past, segmented. The 4 states (loading skeleton /
// empty / error / loaded) via a mock hook; swaps to the player's own Firestore
// query later with the same state handling.
export function MisPartidosPage() {
  const navigate = useNav()
  const { status, matches, tab, setTab, retry } = useMisPartidos()

  return (
    <>
      <TopBar title="Mis partidos" />

      {/* Above the tabs, and outside them: a reserved court is not a "próximo"
          or a "pasado" match, it is a different thing the player owns. Putting
          it inside a tab would hide it behind a filter they did not set. It
          renders nothing at all when there is nothing — see the section. */}
      <CourtBookingsSection />

      <div className="px-4 py-3">
        <SegmentedControl<MisTab>
          value={tab}
          onChange={setTab}
          options={[
            { value: 'proximos', label: 'Próximos' },
            { value: 'pasados', label: 'Pasados' },
          ]}
        />
      </div>

      {status === 'loading' && (
        <div className="flex flex-col gap-3 px-4 pb-6" aria-busy>
          {[0, 1].map((i) => <MatchCardSkeleton key={i} />)}
        </div>
      )}

      {status === 'error' && <ErrorState onRetry={retry} />}

      {status === 'empty' && (
        <EmptyState
          title={tab === 'proximos' ? 'Aún no tienes partidos próximos.' : 'Todavía no has jugado partidos.'}
          cta={
            tab === 'proximos' ? (
              <Button variant="outline" size="pill" onClick={() => navigate('/crear')}>
                <Plus size={18} /> Crear partido
              </Button>
            ) : (
              <Button variant="outline" size="pill" onClick={() => navigate('/partidos')}>
                Explorar partidos
              </Button>
            )
          }
        />
      )}

      {status === 'loaded' && (
        <div className="flex flex-col gap-3 px-4 pb-6">
          {matches.map((m, i) => (
            <div key={m.id} className="um-enter" style={{ '--i': i } as CSSProperties}>
              <MatchCard match={m} />
            </div>
          ))}
        </div>
      )}
    </>
  )
}
